import React, { useState, useMemo } from 'react';
import { Button, Input } from '@/components/atoms';
import { createProductSchema, type CreateProduct } from '@/schemas/products';
import {
  useCreateProductMutation,
  useUpdateProductMutation,
  useCategoriesList,
  useSubcategoriesByCategory,
} from '@/hooks/useErpApi';
import type { Product } from '@/schemas/products';
import { AttachmentList } from '@/components/organisms/Attachment/AttachmentList';

// Type definitions for category/subcategory
interface Category {
  id: string;
  name: string;
  description?: string;
}

interface Subcategory {
  id: string;
  name: string;
  description?: string;
}

interface ProductFormProps {
  initialData?: Product;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const ProductForm: React.FC<ProductFormProps> = ({ initialData, onSuccess, onCancel }) => {
  const { mutate: createProduct, isLoading: createLoading } = useCreateProductMutation();
  const { mutate: updateProduct, isLoading: updateLoading } = useUpdateProductMutation();
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState<CreateProduct>({
    name: initialData?.name || '',
    description: initialData?.description || '',
    category_id: initialData?.category_id || undefined,
    subcategory_id: initialData?.subcategory_id || undefined,
  });

  const { data: categoriesData, isLoading: categoriesLoading } = useCategoriesList();
  
  // Only fetch subcategories when a category is selected
  // Use a stable value to prevent unnecessary re-renders
  const selectedCategoryId = formData.category_id || '';
  const { data: subcategoriesData, isLoading: subcategoriesLoading } = useSubcategoriesByCategory(
    selectedCategoryId
  );
  
  const isLoading = createLoading || updateLoading || categoriesLoading;

  // Helper to extract array from various API response shapes
  function extractArray<T>(data: unknown): T[] {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    const d = data as any;
    if (d?.data && Array.isArray(d.data)) return d.data;
    if (d?.data?.data && Array.isArray(d.data.data)) return d.data.data;
    // Check for named keys (subcategories, items, etc.)
    for (const key of Object.keys(d)) {
      if (Array.isArray(d[key]) && key !== 'pagination') return d[key];
    }
    return [];
  }

  // Extract categories and subcategories from API responses
  const categories = useMemo<Category[]>(() => extractArray<Category>(categoriesData), [categoriesData]);
  
  const subcategories = useMemo<Subcategory[]>(() => extractArray<Subcategory>(subcategoriesData), [subcategoriesData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      // Clear subcategory when category changes
      if (name === 'category_id') {
        return { ...prev, [name]: value || undefined, subcategory_id: undefined };
      }
      return { ...prev, [name]: value || undefined };
    });
    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setValidationErrors({});

    try {
      const validated = createProductSchema.parse(formData);

      if (initialData) {
        await updateProduct({ id: initialData.id, data: validated });
      } else {
        await createProduct(validated);
      }

      onSuccess?.();
    } catch (err) {
      if (err && typeof err === 'object' && 'errors' in err) {
        const zodError = err as { errors: Array<{ path: (string | number)[]; message: string }> };
        const errMap: Record<string, string> = {};
        zodError.errors.forEach((e) => {
          const field = String(e.path[0]);
          errMap[field] = e.message;
        });
        setValidationErrors(errMap);
      } else {
        setError(err instanceof Error ? err.message : 'An error occurred');
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="bg-error-50 border border-error-200 rounded-lg p-4">
          <p className="text-error-700">{error}</p>
        </div>
      )}

      <div>
        <label htmlFor="name" className="block text-sm font-medium text-neutral-700 mb-2">
          Product Name *
        </label>
        <Input
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="Enter product name"
          disabled={isLoading}
        />
        {validationErrors.name && (
          <p className="text-error-600 text-sm mt-1">{validationErrors.name}</p>
        )}
      </div>

      <div>
        <label htmlFor="description" className="block text-sm font-medium text-neutral-700 mb-2">
          Description
        </label>
        <textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          placeholder="Enter product description"
          disabled={isLoading}
          className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-neutral-100 disabled:cursor-not-allowed"
          rows={4}
        />
        {validationErrors.description && (
          <p className="text-error-600 text-sm mt-1">{validationErrors.description}</p>
        )}
      </div>

      {/* Category and Subcategory Selection */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="category_id" className="block text-sm font-medium text-neutral-700 mb-2">
            Category
          </label>
          <select
            id="category_id"
            name="category_id"
            value={formData.category_id || ''}
            onChange={handleChange}
            disabled={isLoading || categoriesLoading}
            className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-neutral-100 disabled:cursor-not-allowed"
          >
            <option value="">Select a category (optional)</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
                {category.description ? ` - ${category.description}` : ''}
              </option>
            ))}
          </select>
          {validationErrors.category_id && (
            <p className="text-error-600 text-sm mt-1">{validationErrors.category_id}</p>
          )}
          <p className="text-xs text-neutral-500 mt-1">
            Categorize this product for better organization
          </p>
        </div>

        <div>
          <label htmlFor="subcategory_id" className="block text-sm font-medium text-neutral-700 mb-2">
            Subcategory
          </label>
          <select
            id="subcategory_id"
            name="subcategory_id"
            value={formData.subcategory_id || ''}
            onChange={handleChange}
            disabled={isLoading || !formData.category_id || subcategoriesLoading}
            className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-neutral-100 disabled:cursor-not-allowed"
          >
            <option value="">Select a subcategory (optional)</option>
            {subcategories.map((subcategory) => (
              <option key={subcategory.id} value={subcategory.id}>
                {subcategory.name}
                {subcategory.description ? ` - ${subcategory.description}` : ''}
              </option>
            ))}
          </select>
          {validationErrors.subcategory_id && (
            <p className="text-error-600 text-sm mt-1">{validationErrors.subcategory_id}</p>
          )}
          <p className="text-xs text-neutral-500 mt-1">
            {formData.category_id
              ? 'Select a subcategory for this category'
              : 'Select a category first'}
          </p>
        </div>
      </div>

      {initialData?.id && (
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">Product Images</label>
          <AttachmentList
            entityType="product"
            entityId={initialData.id}
            allowUpload={true}
            allowDelete={true}
            viewMode="grid"
          />
        </div>
      )}

      {!initialData && (
        <div className="bg-info-50 border border-info-200 rounded-lg p-4">
          <p className="text-info-700 text-sm">
            Product images can be uploaded after the product is created.
          </p>
        </div>
      )}

      <div className="flex gap-2 justify-end">
        {onCancel && (
          <Button type="button" variant="secondary" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
        )}
        <Button type="submit" variant="primary" disabled={isLoading}>
          {isLoading ? 'Saving...' : initialData ? 'Update' : 'Create'} Product
        </Button>
      </div>
    </form>
  );
};
